'use client';

import { PencilIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/solid';
import { Button } from '@koda/koda-components/atoms/Button';
import { Card } from '@koda/koda-components/atoms/Card';
import ModalDialogue from '@koda/koda-components/molecules/ModalDialogue';
import NumberField from '@koda/koda-components/molecules/NumberField';
import TextField from '@koda/koda-components/molecules/TextField';
import { cn } from '@koda/koda-components/utils/classnameVariants';
import { useTranslations } from 'next-intl';
import { ReactElement, useEffect, useState } from 'react';
import { useLicenseStore } from '../../../licenseStore';
import { LicenseTypes } from '../../../types/licenses';
import { useLicenseBusiness } from '../hooks/useLicenseBusiness';
import {
  createTheaterBusinessInformationDefaults,
  THEATER_CONTENT_PATH,
  theaterBusinessInformationSchema,
  WorkOption,
  workOptionSchema,
} from './theater.Schema';

export const TheaterBusinessForm = (): ReactElement => {
  const t = useTranslations(THEATER_CONTENT_PATH);

  const { values, setField, showErrors, errors, isValid } = useLicenseBusiness(LicenseTypes.Theater, {
    schema: theaterBusinessInformationSchema,
    defaults: createTheaterBusinessInformationDefaults,
  });
  const { setShowErrors } = useLicenseStore();

  const isFirst = values.works.length < 1;

  const [editingIndex, setEditingIndex] = useState<number>(isFirst ? 0 : -1);
  const [addingNewWork, setAddingNewWork] = useState<boolean>(isFirst);
  const [deletingIndex, setDeletingIndex] = useState<number>(-1);
  const [modal, setModal] = useState<boolean>(false);

  const createEmptyWork = (): WorkOption => ({
    workTitle: '',
    composer: '',
    workNumber: '',
    usageDuration: '',
  });

  const isEmptyWork = (work: WorkOption | undefined): boolean => {
    if (!work) return true;
    return !work.workTitle?.trim() && !work.composer?.trim() && !work.workNumber?.trim() && !work.usageDuration?.trim();
  };

  useEffect(() => {
    // If the user navigates away/back while an empty placeholder work exists,
    // we don't want it to show up as a "blank work" summary card.
    const nonEmptyWorks = values.works.filter(w => !isEmptyWork(w));

    if (nonEmptyWorks.length !== values.works.length) {
      setField('works', nonEmptyWorks);
    }

    // Initialize with one empty work in edit mode if none exist.
    if (nonEmptyWorks.length < 1) {
      setEditingIndex(0);
      setAddingNewWork(true);
      setField('works', [createEmptyWork()]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveWork = (work: WorkOption, index: number): void =>
    setField(
      'works',
      values.works.map((w, i) => (i === index ? work : w)),
    );

  const getWorkFieldError = (index: number, field: keyof WorkOption) => {
    if (!showErrors) return undefined;
    const path = `works.${index}.${field}`;
    const errKey = errors?.[path]?.[0];
    return errKey ? t(errKey) : undefined;
  };

  const addEmptyWork = (): void => {
    setShowErrors(!isValid);
    if (!isValid) return;
    const work = createEmptyWork();
    setEditingIndex(values.works.length);
    setField('works', [...values.works, work]);
    setAddingNewWork(true);
  };

  const removeWork = (index: number): void => {
    if (values.works.length === 1) {
      setField('works', [createEmptyWork()]);
      setEditingIndex(0);
      setAddingNewWork(true);
      return;
    }

    const newWorks = values.works.filter((_, i) => i !== index);
    setField('works', newWorks);

    if (editingIndex === index) {
      setEditingIndex(-1);
      setAddingNewWork(false);
    } else if (editingIndex > index) {
      setEditingIndex(editingIndex - 1);
    }
  };

  const isEditValid = (work: WorkOption | undefined): boolean => {
    if (!work || !workOptionSchema.safeParse(work).success) return false;
    return true;
  };
  const validEdit = isEditValid(values.works[editingIndex]);

  const changeEdit = (index: number): void => {
    if (editingIndex === -1) {
      setEditingIndex(index);
      return;
    }
    setShowErrors(!validEdit);

    if (!validEdit) return;
    setEditingIndex(index);
    setAddingNewWork(false);
  };

  return (
    <>
      {values.works.map(
        (work, index) =>
          work &&
          (editingIndex === index ? (
            <Card
              key={index}
              backgroundColor="white"
              hue={100}
              className={cn('relative flex flex-col gap-6 px-6 py-8', addingNewWork ? '' : 'border-2')}
            >
              <p className="font-graphik text-lg font-bold leading-graphik">
                {addingNewWork ? t('addWork') : work.workTitle}
              </p>
              {!(index === 0 && addingNewWork) && (
                <button
                  onClick={() => {
                    setDeletingIndex(index);
                    setModal(true);
                  }}
                  className="absolute right-6 top-6"
                >
                  <XMarkIcon className="size-6" />
                </button>
              )}
              <TextField
                label={t('workTitle.label')}
                name="workTitle"
                styleVariant="border"
                type="text"
                value={work?.workTitle ?? ''}
                onChange={e => saveWork({ ...work, workTitle: e.target.value }, index)}
                error={getWorkFieldError(index, 'workTitle')}
                data-error-field={!!getWorkFieldError(index, 'workTitle')}
              />
              <TextField
                label={t('composer.label')}
                name="composer"
                styleVariant="border"
                type="text"
                onlyText
                value={work?.composer ?? ''}
                onChange={e => saveWork({ ...work, composer: e.target.value }, index)}
                data-error-field={!!getWorkFieldError(index, 'composer')}
                error={getWorkFieldError(index, 'composer')}
              />
              <NumberField
                label={t('workNumber.label')}
                name="workNumber"
                styleVariant="border"
                helpText={t('workNumber.helpText')}
                optionalText={t('workNumber.optionalText')}
                value={work?.workNumber ?? ''}
                onChange={e => saveWork({ ...work, workNumber: e.target.value }, index)}
                data-error-field={!!getWorkFieldError(index, 'workNumber')}
                error={getWorkFieldError(index, 'workNumber')}
              />
              <Button
                variant="inlineLink"
                href={t('workDatabaseLink')}
                target="_blank"
                buttonType="link"
                className="w-fit"
              >
                <ArrowTopRightOnSquareIcon className="mr-1 inline size-4" />
                {t('searchWorkDatabase')}
              </Button>
              <TextField
                label={t('usageDuration.label')}
                name="usageDuration"
                styleVariant="border"
                placeholder={t('usageDuration.placeholder')}
                helpText={t('usageDuration.helpText')}
                type="text"
                value={work?.usageDuration ?? ''}
                onChange={e => saveWork({ ...work, usageDuration: e.target.value }, index)}
                data-error-field={!!getWorkFieldError(index, 'usageDuration')}
                error={getWorkFieldError(index, 'usageDuration')}
              />
            </Card>
          ) : (
            <button key={index} onClick={() => changeEdit(index)}>
              <Card
                backgroundColor="white"
                hue={100}
                className="flex flex-col gap-1 p-6 text-left font-graphik text-sm leading-graphik"
              >
                <div className="flex justify-between">
                  <p className="text-lg font-bold">{work.workTitle ?? ''}</p>
                  <div className="flex gap-1 sm:gap-2">
                    <PencilIcon className="m-1 size-4" />
                    <button
                      onClick={() => {
                        setDeletingIndex(index);
                        setModal(true);
                      }}
                    >
                      <XMarkIcon className="size-5" />
                    </button>
                  </div>
                </div>
                <p>
                  {t('composer.label')}: {work.composer}
                </p>
                <p>
                  {t('workNumber.label')}: {work.workNumber ? work.workNumber : t('workNumber.emptyText')}
                </p>
                <p>
                  {t('usageDuration.label')}: {work.usageDuration}
                </p>
              </Card>
            </button>
          )),
      )}

      <Button buttonType="button" variant="secondaryDark" onClick={addEmptyWork} className="flex gap-1">
        <PlusIcon className="size-5" />
        {t('addAnotherWorkButton')}
      </Button>

      <ModalDialogue
        open={modal}
        isClosable={true}
        title={t('removeModal.title')}
        type="info"
        onOpenChange={value => {
          if (!value) {
            setDeletingIndex(-1);
            setModal(false);
          }
        }}
        primaryText={t('removeModal.removeButton')}
        onPrimaryAction={() => {
          removeWork(deletingIndex);
          setDeletingIndex(-1);
          setModal(false);
        }}
        cancelText={t('removeModal.cancelButton')}
        onCancelAction={() => {
          setDeletingIndex(-1);
          setModal(false);
        }}
      />
    </>
  );
};
